import { useMemo } from 'react';

import { AiRestApiFactory } from '@/api/generated/apis/ai-rest-api';
import { DictionariesApiFactory } from '@/api/generated/apis/dictionaries-api';
import { DictionariesSearchRestApiFactory } from '@/api/generated/apis/dictionaries-search-rest-api';
import { DictionariesV2ApiFactory } from '@/api/generated/apis/dictionaries-v2-api';
import { EmployeesSearchRestApiFactory } from '@/api/generated/apis/employees-search-rest-api';
import { FilesApiFactory } from '@/api/generated/apis/files-api';
import { FrontendErrorsApiFactory } from '@/api/generated/apis/frontend-errors-api';
import { LocationCodesRestApiFactory } from '@/api/generated/apis/location-codes-rest-api';
import { LogoutApiFactory } from '@/api/generated/apis/logout-api';
import { NotificationsApiFactory } from '@/api/generated/apis/notifications-api';
import { PrivacyPolicyApiFactory } from '@/api/generated/apis/privacy-policy-api';
import { RecruitmentBoardApiFactory } from '@/api/generated/apis/recruitment-board-api';
import { ReportsApiFactory } from '@/api/generated/apis/reports-api';
import { SessionApiFactory } from '@/api/generated/apis/session-api';
import { StatisticsRestApiFactory } from '@/api/generated/apis/statistics-rest-api';
import { UserInitializeApiFactory } from '@/api/generated/apis/user-initialize-api';
import { UserLandingPagePreferenceRestApiFactory } from '@/api/generated/apis/user-landing-page-preference-rest-api.ts';
import { UserPrivacyPolicyApiFactory } from '@/api/generated/apis/user-privacy-policy-api';
import { UserToursRestApiFactory } from '@/api/generated/apis/user-tours-rest-api';
import { UsersApiFactory } from '@/api/generated/apis/users-api';
import { UsersCompanyDataApiFactory } from '@/api/generated/apis/users-company-data-api';
import { UsersEraApiFactory } from '@/api/generated/apis/users-era-api';
import { VacancyCandidatesApiFactory } from '@/api/generated/apis/vacancy-candidates-api';
import { VacancyCandidatesSearchRestApiFactory } from '@/api/generated/apis/vacancy-candidates-search-rest-api';
import { VacancyCompaniesApiFactory } from '@/api/generated/apis/vacancy-companies-api';
import { type Configuration } from '@/api/generated/configuration.ts';
import CONFIG from '@/config/config';

import { useAxiosInstance } from './useAxiosInstance.util';

interface Return {
  aiApi: ReturnType<typeof AiRestApiFactory>;
  dictionariesApi: ReturnType<typeof DictionariesApiFactory>;
  dictionariesSearchRestApi: ReturnType<typeof DictionariesSearchRestApiFactory>;
  dictionariesV2Api: ReturnType<typeof DictionariesV2ApiFactory>;
  employeesSearchRestApi: ReturnType<typeof EmployeesSearchRestApiFactory>;
  filesApi: ReturnType<typeof FilesApiFactory>;
  frontendErrorsApi: ReturnType<typeof FrontendErrorsApiFactory>;
  locationCodesApi: ReturnType<typeof LocationCodesRestApiFactory>;
  logoutApi: ReturnType<typeof LogoutApiFactory>;
  notificationApi: ReturnType<typeof NotificationsApiFactory>;
  privacyPolicyApi: ReturnType<typeof PrivacyPolicyApiFactory>;
  recruitmentBoardApi: ReturnType<typeof RecruitmentBoardApiFactory>;
  reportsApi: ReturnType<typeof ReportsApiFactory>;
  sessionApi: ReturnType<typeof SessionApiFactory>;
  statisticsApi: ReturnType<typeof StatisticsRestApiFactory>;
  userInitializeApi: ReturnType<typeof UserInitializeApiFactory>;
  userLandingPagePreferenceRestApi: ReturnType<typeof UserLandingPagePreferenceRestApiFactory>;
  userPrivacyPolicyApi: ReturnType<typeof UserPrivacyPolicyApiFactory>;
  usersApi: ReturnType<typeof UsersApiFactory>;
  usersCompanyDataApi: ReturnType<typeof UsersCompanyDataApiFactory>;
  usersEraApi: ReturnType<typeof UsersEraApiFactory>;
  userToursApi: ReturnType<typeof UserToursRestApiFactory>;
  vacancyCandidatesApi: ReturnType<typeof VacancyCandidatesApiFactory>;
  vacancyCandidatesSearchApi: ReturnType<typeof VacancyCandidatesSearchRestApiFactory>;
  vacancyCompaniesApi: ReturnType<typeof VacancyCompaniesApiFactory>;
}

export const useApiClient = (): Return => {
  const basePath = CONFIG.HOST;
  const configuration = getApiConfiguration(basePath);
  const axiosInstance = useAxiosInstance();

  return useMemo(() => {
    return {
      aiApi: AiRestApiFactory(configuration, undefined, axiosInstance),
      dictionariesApi: DictionariesApiFactory(configuration, undefined, axiosInstance),
      dictionariesSearchRestApi: DictionariesSearchRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      dictionariesV2Api: DictionariesV2ApiFactory(configuration, undefined, axiosInstance),
      employeesSearchRestApi: EmployeesSearchRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      filesApi: FilesApiFactory(configuration, undefined, axiosInstance),
      frontendErrorsApi: FrontendErrorsApiFactory(configuration, undefined, axiosInstance),
      locationCodesApi: LocationCodesRestApiFactory(configuration, undefined, axiosInstance),
      logoutApi: LogoutApiFactory(configuration, undefined, axiosInstance),
      notificationApi: NotificationsApiFactory(configuration, undefined, axiosInstance),
      privacyPolicyApi: PrivacyPolicyApiFactory(configuration, undefined, axiosInstance),
      recruitmentBoardApi: RecruitmentBoardApiFactory(configuration, undefined, axiosInstance),
      reportsApi: ReportsApiFactory(configuration, undefined, axiosInstance),
      sessionApi: SessionApiFactory(configuration, undefined, axiosInstance),
      statisticsApi: StatisticsRestApiFactory(configuration, undefined, axiosInstance),
      userInitializeApi: UserInitializeApiFactory(configuration, undefined, axiosInstance),
      userLandingPagePreferenceRestApi: UserLandingPagePreferenceRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      userPrivacyPolicyApi: UserPrivacyPolicyApiFactory(configuration, undefined, axiosInstance),
      usersApi: UsersApiFactory(configuration, undefined, axiosInstance),
      usersCompanyDataApi: UsersCompanyDataApiFactory(configuration, undefined, axiosInstance),
      usersEraApi: UsersEraApiFactory(configuration, undefined, axiosInstance),
      userToursApi: UserToursRestApiFactory(configuration, undefined, axiosInstance),
      vacancyCandidatesApi: VacancyCandidatesApiFactory(configuration, undefined, axiosInstance),
      vacancyCandidatesSearchApi: VacancyCandidatesSearchRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      vacancyCompaniesApi: VacancyCompaniesApiFactory(configuration, undefined, axiosInstance),
    };
  }, [axiosInstance, configuration]);
};

export const getApiConfiguration = (basePath: string): Configuration => ({
  basePath,
  isJsonMime(_mime: string): boolean {
    return true;
  },
});
